import { useLocation, useNavigate } from 'react-router-dom';
import {
  AccountBalance,
  AddBusiness,
  Analytics,
  AssignmentTurnedIn,
  AutoStories,
  Bolt,
  Chat,
  Campaign,
  Explore,
  FactCheck,
  Gavel,
  Headphones,
  Hub,
  Insights,
  Inventory,
  Key,
  RateReview,
  Schema,
  Science,
  Security,
  SmartToy,
  Sync,
  Transform,
  TrendingUp,
} from '@mui/icons-material';
import { Box, Divider, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';

import { useUserRoles } from '@features/user';

import { RecentEntitiesNav } from './recent-entities-nav';

const navItems = [
  { sectionLabel: 'Application' },
  { path: '/admin-v2/intake-hub', label: 'Intake Hub', icon: AssignmentTurnedIn },
  { path: '/admin-v2/feedback', label: 'Feedback', icon: RateReview },
  { path: '/admin-v2/shadow-notes', label: 'Shadow Notes', icon: Science },
  { path: '/admin-v2/template-model-conversion', label: 'Template Conversion', icon: Transform },
  { path: '/admin-v2/encounter-telemetry-sessions', label: 'Telemetry', icon: Analytics },
  { path: '/admin-v2/demo-org', label: 'Demo Org', icon: AddBusiness },
  { divider: true },
  // Product config — the settings that change how the product behaves for
  // customers, previously scattered through "Application" (GDN-2777).
  { sectionLabel: 'Product config' },
  { path: '/admin-v2/capabilities', label: 'Capabilities', icon: Security, alsoMatch: ['/admin-v2/capability/'] },
  { path: '/admin-v2/bundles', label: 'Bundles', icon: Inventory, alsoMatch: ['/admin-v2/bundle/'] },
  { path: '/admin-v2/feature-explorer', label: 'Feature Explorer', icon: Explore },
  { path: '/admin-v2/system-notifications', label: 'System Notifications', icon: Campaign },
  { divider: true },
  // Cortex — citation-grounded policy / intelligence layer. Keeps the surfaces
  // you act on; its two browsers moved to Reference.
  { sectionLabel: 'Cortex' },
  { path: '/admin-v2/cortex/policies', label: 'Policies', icon: Gavel },
  { path: '/admin-v2/streaming-threads', label: 'Streaming Threads', icon: Chat, alsoMatch: ['/admin-v2/streaming-thread/'] },
  { path: '/admin-v2/eval-results', label: 'Eval Results', icon: FactCheck },
  { divider: true },
  { sectionLabel: 'Billing' },
  { path: '/admin-v2/rcm-scoreboard', label: 'RCM Scoreboard', icon: AccountBalance },
  { path: '/admin-v2/morning-worklist', label: 'Morning Worklist', icon: Bolt },
  { path: '/admin-v2/billing-dashboard', label: 'Billing Dashboard', icon: AccountBalance },
  { path: '/admin-v2/billing-tasks', label: 'Billing Tasks', icon: FactCheck },
  { path: '/admin-v2/payer-connections', label: 'Payer Connections', icon: Hub },
  { path: '/admin-v2/payers/sync', label: 'Payer Sync', icon: Sync },
  { path: '/admin-v2/eligibility-workqueue', label: 'Eligibility Queue', icon: FactCheck },
  { divider: true },
  { sectionLabel: 'Sales' },
  { path: '/admin-v2/prospects', label: 'Sales Prospects', icon: TrendingUp, alsoMatch: ['/admin-v2/prospect/'] },
  { path: '/admin-v2/sales-scoreboard', label: 'Sales Scoreboard', icon: TrendingUp },
  { divider: true },
  { path: '/admin-v2/agent', label: 'Agent', icon: SmartToy },
  { path: '/admin-v2/mcp-access', label: 'MCP Access', icon: Key },
  // Token registry is admin-only — hidden from moderators (adminOnly flag)
  { path: '/admin-v2/mcp-tokens', label: 'MCP Tokens', icon: Security, adminOnly: true },
  { divider: true },
  // Reference — things you read rather than operate (GDN-2777).
  { sectionLabel: 'Reference' },
  { path: '/admin-v2/event-catalog', label: 'Event Catalog', icon: Insights },
  { path: '/admin-v2/cortex/resources', label: 'Resource Types', icon: Schema },
  { path: '/admin-v2/analytics-docs', label: 'Analytics Docs', icon: AutoStories },
];

export function AdminSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const roles = useUserRoles();
  const isAdmin = roles.includes('Admin');

  // `startsWith` alone leaves the sidebar blank on a singular detail route
  // (/admin-v2/bundle/:id never matches /admin-v2/bundles), so items whose
  // detail page has a different stem list it in `alsoMatch` (GDN-2777).
  const isActive = (path: string, alsoMatch?: string[]) =>
    location.pathname.startsWith(path) ||
    (alsoMatch ?? []).some(prefix => location.pathname.startsWith(prefix));
  const visibleItems = navItems.filter(item => !('adminOnly' in item && item.adminOnly) || isAdmin);

  return (
    <aside className="w-60 bg-white border-r border-gray-200 flex flex-col h-full">
      {/* Header */}
      <button
        type="button"
        className="h-16 w-full flex items-center px-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 text-left"
        onClick={() => navigate('/admin-v2')}
      >
        <Headphones className="h-8 w-8 text-blue-600" />
        <span className="ml-3 text-xl font-semibold text-gray-900">Admin v2</span>
      </button>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2">
        <RecentEntitiesNav />
        <List component="nav" disablePadding>
          {visibleItems.map((item, index) => {
            if ('divider' in item) {
              return <Divider key={`divider-${index}`} sx={{ my: 1 }} />;
            }
            if ('sectionLabel' in item) {
              return (
                <Box key={`section-${index}`} sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                    }}
                  >
                    {item.sectionLabel}
                  </Typography>
                </Box>
              );
            }

            const Icon = item.icon;
            const active = isActive(item.path, 'alsoMatch' in item ? item.alsoMatch : undefined);

            return (
              <ListItemButton
                key={item.path}
                onClick={() => navigate(item.path)}
                selected={active}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.50',
                    '&:hover': {
                      backgroundColor: 'primary.100',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Icon fontSize="small" color={active ? 'primary' : 'inherit'} />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  slotProps={{
                    primary: {
                      color: active ? 'primary.main' : 'text.primary',
                      sx: {
                        fontSize: '0.875rem',
                        fontWeight: active ? 600 : 400,
                      },
                    },
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </nav>
    </aside>
  );
}
